import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';

'use strict';

import {ViewChild, Component, OnInit} from '@angular/core';
import {FormGroup, FormControl, Validators, FormBuilder} from "@angular/forms";
import {PasswordValidators} from "../services/password.validators";
import {ToasterService} from "angular2-toaster";
import {TranslocoService} from '@jsverse/transloco';

import {ActivePromotionConfig} from "../config/active-promotion.config";
import {FormHelperService} from "../services/form-helper.service";
import {DataService} from "@cosbeni/database";
import {OrientDBCollectionView} from "@cosbeni/database";
import {ORole, OUser} from "@cosbeni/database";
import {ROLES} from "../services/roles";


@Component({
    standalone: false,
    selector: 'cj-admin-user',
    templateUrl: 'user-management.component.html'
})

export class UserManagementComponent implements OnInit {
    createForm: FormGroup;
    editForm: FormGroup;
    passwordForm: FormGroup;
    readonly: Boolean = true;
    @ViewChild('grid') grid: wjcGrid.FlexGrid;
    users: OrientDBCollectionView;
    roles: ORole[];
    role: ORole;
    /** 割当可能ロール (オンラインオークションユーザ / オンラインオークション管理者) のみ。 */
    assignableRoles: ORole[] = [];
    selectedRoleName: string = ROLES.ONLINE_USER;
    user: any = new OUser();
    password: string = '';
    mode: string = "view";

    /** この画面が割当できるオンラインラインのロール名。 */
    private readonly onlineRoleNames: string[] = [ROLES.ONLINE_USER, ROLES.ONLINE_ADMIN];

    constructor(public dataService: DataService, public toasterService: ToasterService, public formHelper: FormHelperService, public formBuilder: FormBuilder, private transloco: TranslocoService) {
        this.users = new OrientDBCollectionView(dataService, ActivePromotionConfig.DATABASE, "SELECT FROM OUser", "OUser", "", 0, 100, true, true, false)
        this.users.url = ActivePromotionConfig.SERVER_URL;
        this.users.tokenName = ActivePromotionConfig.DATABASE + "_token";
        // 一覧をオンラインラインのユーザ (オンラインオークションユーザ / 管理者) のみに限定する。
        this.users.filterSpecial = "roles CONTAINS (name IN ['" + ROLES.ONLINE_USER + "','" + ROLES.ONLINE_ADMIN + "'])";
        this.users.filterOnServer = true;
    }

    ngOnInit() {
        this.users.currentChanged.addHandler(this.onCurrentChanged, this);
        this.createForm = this.formBuilder.group({
            username: new FormControl('', [Validators.required, Validators.minLength(3)]),
            memberName: new FormControl('', [Validators.required]),
            administratorName: new FormControl('', [Validators.required]),
            telephone: new FormControl('', [Validators.required]),
            fax: new FormControl('', []),
            address: new FormControl('', [Validators.required]),
            password: new FormGroup({
                newPassword: new FormControl('', [Validators.required, Validators.minLength(3)]),
                confirmPassword: new FormControl('', [])
            }, PasswordValidators.mismatchedPasswords())
        })
        this.editForm = this.formBuilder.group({
            username: new FormControl('', [Validators.required, Validators.minLength(3)]),
            memberName: new FormControl('', [Validators.required]),
            administratorName: new FormControl('', [Validators.required]),
            telephone: new FormControl('', [Validators.required]),
            fax: new FormControl('', []),
            address: new FormControl('', [Validators.required])
        })
        this.passwordForm = this.formBuilder.group({
            password: new FormGroup({
                newPassword: new FormControl('', [Validators.required, Validators.minLength(3)]),
                confirmPassword: new FormControl('', [])
            }, PasswordValidators.mismatchedPasswords())
        })
        this.dataService.query({sql: "SELECT FROM ORole", count: "SELECT FROM ORole"}, ActivePromotionConfig.SERVER_URL).subscribe(response => {
            this.roles = wjcCore.isArray(response.results) ? response.results : [];
            this.assignableRoles = this.roles.filter(role => this.onlineRoleNames.indexOf(role.name) >= 0);
            // 既定ロールはオンラインオークションユーザ。
            this.role = this.roleFor(ROLES.ONLINE_USER);
        })
    }

    /** 選択中のロール名に対応する ORole ドキュメントを返す。 */
    private roleFor(name: string): ORole {
        if (wjcCore.isArray(this.roles)) {
            for (let role of this.roles) {
                if (role.name === name) {
                    return role;
                }
            }
        }
        return null;
    }

    onCurrentChanged(sender: any, args: wjcCore.EventArgs): void {
        if (this.users.currentItem != null) {
            this.editForm.reset();
            this.user = Object.assign({}, this.users.currentItem);
            // 既存ユーザがオンラインロールを持っていればそれを選択、無ければ既定。
            this.selectedRoleName = ROLES.ONLINE_USER;
            if (wjcCore.isArray(this.user.roles)) {
                for (let role of this.user.roles) {
                    if (role && this.onlineRoleNames.indexOf(role.name) >= 0) {
                        this.selectedRoleName = role.name;
                    }
                }
            }
        }
    }

    editUser(): void {
        this.mode = "edit"
    }

    confirmUser(): void {
        this.mode = "confirm"
    }

    createUser(): void {
        this.mode = "edit"
        this.user = new OUser();
        this.selectedRoleName = ROLES.ONLINE_USER;
    }

    saveUser(): void {
        // 選択中のオンラインロールを割当 (既存ロールは置き換える)。
        let selected = this.roleFor(this.selectedRoleName);
        if (selected != null) {
            this.user.roles = [selected];
        }
        let sql = "BEGIN\n"
        let user = Object.assign({}, this.user);
        let properties = [];
        for (let key in user) {
            if (key.indexOf("@") == 0)
                continue;
            if (user['@rid'] != "#-1:-1" && key == "password")
                continue;
            if (key == "buyer" || key == "seller") {
                if (wjcCore.isBoolean(user[key]))
                    properties.push(key + "=" + user[key]);
                if (user[key].hasOwnProperty("@rid"))
                    properties.push(key + "=" + user[key]['@rid']);
            } else if (key == "roles") {
                let roles = []
                if (wjcCore.isArray(user[key])) {
                    for (let role of user[key]) {
                        if (role.hasOwnProperty("@rid")) {
                            roles.push(role['@rid']);
                        }
                    }
                }
                properties.push(key + "=[" + roles.join(",") + "]");
            } else {
                if (wjcCore.isString(user[key]) && !wjcCore.isNullOrWhiteSpace(user[key])) {
                    let v: string = user[key];
                    v = v.replace(/\\/g, "\\\\");
                    v = v.replace(/\n/g, "\\n");
                    v = v.replace(/\t/g, "\\t");
                    v = v.replace(/'/g, "\\'");
                    v = v.replace(/"/g, '\\"');
                    user[key] = v;
                }
                if (user[key] != null) {
                    properties.push(key + "='" + user[key] + "'")
                }
            }
        }
        if (user['@rid'] == "#-1:-1") {
            sql = sql + "INSERT INTO OUser SET " + properties.join(",") + "\n";
        } else {
            sql = sql + "UPDATE OUser SET " + properties.join(",") + " WHERE @rid = " + user['@rid'] + "\n";
        }
        sql = sql + "COMMIT RETRY 100\n";
        this.dataService.batch({sql: sql}, ActivePromotionConfig.SERVER_URL).subscribe((response: any) => {
            this.mode = "view";
            this.toasterService.pop('info', this.transloco.translate('common.save'), this.transloco.translate('admin.saved_message'));
            this.users.queryData();
        }, (error: any) => {
            this.toasterService.pop('error', this.transloco.translate('common.save'), this.transloco.translate('admin.failed_message'));
        })
    }

    changePassword(): void {
        let command = "UPDATE  " + this.user['@rid'] + " SET password = '" + this.password + "'";
        this.dataService.command({sql: command}, ActivePromotionConfig.SERVER_URL).subscribe((response: any) => {
            this.mode = "view";
            this.toasterService.pop('info', this.transloco.translate('admin.change_password'), this.transloco.translate('admin.changed_message'));
            this.users.queryData();
        }, (error: any) => {
            this.toasterService.pop('error', this.transloco.translate('admin.change_password'), this.transloco.translate('admin.failed_message'));
        })
    }

}

